import { User, Award, TrendingUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { PlayerAvatar } from '@/components/shared/player-avatar';
import { getLevelBadgeColor } from '@/components/shared/skill-level-utils';

interface OverviewSectionProps {
  player: {
    firstName: string;
    lastName: string;
    age: number | null;
    skillLevel: string | null;
    utrRating: number | null;
    attendancePercent: number;
    totalEvaluations: number;
  };
  stats: {
    attendanceStats: { total: number };
    evalAverages: {
      effort: number;
      engagement: number;
      competitionLevel: number;
      attitude: number;
    } | null;
  };
}

export function OverviewSection({ player, stats }: OverviewSectionProps) {
  const overallAvg = stats.evalAverages
    ? ((stats.evalAverages.effort + stats.evalAverages.engagement +
        stats.evalAverages.competitionLevel + stats.evalAverages.attitude) / 4).toFixed(1)
    : null;

  return (
    <div className="bg-card rounded-lg shadow p-6">
      <div className="flex items-start gap-6">
        <PlayerAvatar
          firstName={player.firstName}
          lastName={player.lastName}
          variant="director"
          size="xl"
        />
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-foreground">
            {player.firstName} {player.lastName}
          </h1>
          <div className="flex flex-wrap gap-3 mt-2">
            {player.age !== null && (
              <span className="flex items-center gap-1 text-sm text-muted-foreground">
                <User className="w-4 h-4" />
                Age {player.age}
              </span>
            )}
            {player.skillLevel && (
              <Badge className={getLevelBadgeColor(player.skillLevel)}>
                <Award className="w-3 h-3 mr-1" />
                {player.skillLevel}
              </Badge>
            )}
            {player.utrRating !== null && (
              <span className="flex items-center gap-1 text-sm text-muted-foreground">
                <TrendingUp className="w-4 h-4" />
                UTR {player.utrRating.toFixed(1)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <div className="bg-muted/50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-purple-600">{player.attendancePercent}%</p>
          <p className="text-sm text-muted-foreground">Attendance</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-primary">{stats.attendanceStats.total}</p>
          <p className="text-sm text-muted-foreground">Sessions</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{player.totalEvaluations}</p>
          <p className="text-sm text-muted-foreground">Evaluations</p>
        </div>
        {overallAvg && (
          <div className="bg-muted/50 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{overallAvg}</p>
            <p className="text-sm text-muted-foreground">Avg Score</p>
          </div>
        )}
      </div>
    </div>
  );
}
